import { useMemo } from 'react';
import type { Action, Actor, Module, ResourceContext, Scope } from '@dtable/rbac';
import { canAccessModule, hasPermission } from '@dtable/rbac';
import { useAuthStore } from '../stores/auth-store';

/**
 * Bridges the persisted /auth/me payload into the shared @dtable/rbac helper.
 * Use `can(module, action, scope)` in components to gate buttons/menu items —
 * the same helper the backend PermissionsGuard uses server-side.
 */
export function usePermissions() {
  const me = useAuthStore((s) => s.me);
  const actor: Actor | null = useMemo(() => {
    if (!me) return null;
    return {
      userId: me.user.id,
      employeeId: me.employee?.id ?? null,
      organizationId: me.organization.id,
      departmentId: me.employee?.departmentId ?? null,
      managerChain: me.employee?.managerChain ?? [],
      roleKeys: me.roleKeys,
      permissions: me.permissions.map((p) => ({
        module: p.module as Module,
        action: p.action as Action,
        scope: p.scope,
      })),
    };
  }, [me]);

  const can = (module: Module, action: Action, scope: Scope, resource?: ResourceContext) =>
    actor ? hasPermission(actor, module, action, scope, resource) : false;

  const seesModule = (module: Module) => (actor ? canAccessModule(actor, module) : false);

  return { actor, can, seesModule };
}
